/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Icon';

const IconManager: ComponentManagerFn<'Icon'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Icon'>(platform, 'Icon'),
    type: 'Icon',
    name: 'Icon',
    isChildAllowed: (page, item, childType) =>
      (
        ['TriggerOnClick', 'EventSubscription', 'Popover'] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Default Icon to be used when the attribute value is null',
      },
      size: {
        label: 'Size',
        type: 'number',
        required: true,
        min: 12,
        max: 80,
      },
      color: {
        label: 'Color',
        type: 'ColorExpression',
        placeholder: 'e.g. primary or #444',
      },
      contrast: {
        label: 'Contrast Color',
        type: 'boolean',
        helpText:
          'Turn this on to choose a contrasing color based on the above color prop.',
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        helpText: 'Dynamically set the icon based on this attribute value',
      } as ViewAttributeChooserProp,
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.viewAttribute ?? '',
    category: 'Toolbar',
    icon: (item) => {
      if (item.props.icon?.iconName) {
        return item.props.icon;
      }
      return { iconName: 'icons' };
    },
    generateShortId: (item) => item.icon?.iconName,
    replaceableWith: ['ActionButton', 'Button', 'IconButton'],
    description: `**Overview**<br/>
The Icon component is a visual element used to display symbolic imagery from the system's icon library (e.g., FontAwesome or similar). It provides visual context, navigation cues, or status indicators without relying on text.
Use this component to add standalone visual symbols to your interface, such as a checkmark next to a "Completed" task or a warning triangle next to an error message.

**1. Configuration**<br/>
* **Icon Source:** Select a Static Icon using the picker as a default, or use a **Dynamic Attribute (viewAttribute)** to bind the component to a String field in your Data Store, allowing the icon to change programmatically.
* **Size:** Adjustable numeric size (12px to 80px).
* **Color:** Supports ColorExpression to dynamically style the icon. You can use theme variables (primary), CSS colors (#444), or logical expressions to change color based on state (e.g., Red for "High Priority").

**2. Interactivity**<br/>
While primarily visual, the Icon can become interactive through children.
* **TriggerOnClick:** Turns the icon into a clickable button (e.g., clicking a "Help" icon to run a workflow).
* **Popover:** Displays an info bubble when clicked/hovered.
* **EventSubscription:** Listens for system events to trigger animations or updates.

**3. Comparison**<br/>
* **Icon vs. IconButton:** Use Icon for purely visual elements or simple triggers. Use IconButton when you need a standard button behavior (hover states, ripples, accessibility traits) specifically designed for toolbar actions.

**4. Best Use Cases**<br/>
* **Status Indicators:** A Green Checkmark or Red X based on a record's validation status.
* **Decorative Elements:** Adding a "User" icon next to a "My Profile" header text.
* **Visual Controls:** A clickable "Trash Can" icon in a list row to delete an item.`,
    helpRenders: [
      {
        title: 'Default Icon',
        description: 'Icon rendered with Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        item: {
          itemId: 'a1',
          props: defaultValue,
          children: [],
          type: 'Icon',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Icon',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['icon'],
        clearParentChildren: true,
        item: {
          itemId: 'a2',
          props: {
            icon: { iconName: 'apple', iconType: 'brands' },
          },
          children: [],
          type: 'Icon',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Icon Size',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['size'],
        clearParentChildren: true,
        item: {
          itemId: 'a3',
          props: {
            icon: { iconName: 'apple', iconType: 'brands' },
            size: 40,
          },
          children: [],
          type: 'Icon',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Icon Color',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['color'],
        clearParentChildren: true,
        item: {
          itemId: 'a4',
          props: {
            size: 40,
            icon: { iconName: 'apple', iconType: 'brands' },
            color: { type: 'text', value: 'warning' },
          },
          children: [],
          type: 'Icon',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Tooltip',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['toolTip', 'toolTipPosition', 'toolTipMaxWidth'],
        clearParentChildren: true,
        item: {
          itemId: 'a5',
          props: {
            size: 40,
            icon: { iconName: 'apple', iconType: 'brands' },
            toolTip: { type: 'text', value: 'Hi there!' },
            toolTipPosition: 'top',
          },
          children: [],
          type: 'Icon',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default IconManager;
